import type { Hero, ThemeName } from '../types';

export type ResultTitleLayout = 'vertical' | 'horizontal' | 'dual';
export type ResultContrastTone = 'light' | 'dark';

export type ResultCoverConfig = {
  titleLayout: ResultTitleLayout;
  titleAsset?: string;
  lightArtwork: boolean;
  titleContrast: ResultContrastTone;
  scoreContrast: ResultContrastTone;
  themeClass: string;
  quoteLines: string[];
};

const dualTitleIds = new Set([12]);
const lightArtworkIds = new Set([3, 13, 18, 20, 21]);
// 依据390×844结果页中姓名区和契合度区的实际背景亮度分别选色。
// dark 表示浅背景上使用深墨色；light 表示深背景上使用浅金色。
const darkTitleContrastIds = new Set([3, 6, 8, 13, 17, 18, 20, 21]);
const darkScoreContrastIds = new Set([3, 4, 6, 8, 10, 13, 16, 17, 18, 20, 21]);

const titleAssets: Record<number, string> = {
  1: '/assets/results/titles/01-xie-zheng-title-v1.webp',
  2: '/assets/results/titles/02-gongsun-yin-title-v1.webp',
  3: '/assets/results/titles/03-li-changshou-title-v1.webp',
  4: '/assets/results/titles/04-zhao-ling-title-v1.webp',
  5: '/assets/results/titles/05-xie-yanlai-title-v1.webp',
  6: '/assets/results/titles/06-lei-xiuyuan-title-v1.webp',
  7: '/assets/results/titles/07-zang-hai-title-v1.webp',
  8: '/assets/results/titles/08-wei-shao-title-v1.webp',
  9: '/assets/results/titles/09-jiang-changyang-title-v1.webp',
  10: '/assets/results/titles/10-xu-qian-title-v1.webp',
  11: '/assets/results/titles/11-han-li-title-v1.webp',
  12: '/assets/results/titles/12-nan-heng-li-shiliu-title-v1.webp',
  13: '/assets/results/titles/13-mei-zhuyu-title-v1.webp',
  14: '/assets/results/titles/14-xiao-heng-title-v1.webp',
  15: '/assets/results/titles/15-mu-sheng-title-v1.webp',
  16: '/assets/results/titles/16-song-mo-title-v1.webp',
  17: '/assets/results/titles/17-cui-xingzhou-title-v1.webp',
  18: '/assets/results/titles/18-li-lianhua-title-v1.webp',
  19: '/assets/results/titles/19-di-feisheng-title-v1.webp',
  20: '/assets/results/xiangliu-title-v1.webp',
  21: '/assets/results/titles/21-tushan-jing-title-v1.webp',
  22: '/assets/results/titles/22-cang-xuan-title-v1.webp',
  23: '/assets/results/titles/23-tantai-jin-title-v1.webp',
  24: '/assets/results/titles/24-dongfang-qingcang-title-v1.webp',
  25: '/assets/results/titles/25-ling-buyi-title-v1.webp',
};

const themeClasses: Record<ThemeName, string> = {
  清冷仙系: 'cover-frost',
  温润公子系: 'cover-gentle',
  智谋权谋系: 'cover-strategy',
  铁血将军系: 'cover-general',
  危险宿命系: 'cover-danger',
  鲜活成长系: 'cover-vivid',
};

const quoteLines: Record<number, string[]> = {
  1: ['我以铁血守山河，', '你以温柔护余生。'],
  2: ['别人家的盖世英雄负责拔刀，', '你这个更擅长先把', '整盘棋看明白。'],
  3: ['智者未必能胜，', '然善谋者，', '常立不败之地。'],
  4: ['别人家的英雄出生就是侯爷，', '你家的比较励志——', '得从第一集自己往上爬。'],
  5: ['系统没有给你分一个', '出厂满级男主，', '而是会一路长成英雄的人。'],
  6: ['好消息：他确实是冲你来的。', '坏消息：最开始可能', '不是因为爱情。'],
  7: ['别人谈恋爱走一步看一步，', '他可能连你们三年后的风险，', '都顺手算了。'],
  8: ['这个人最开始有多难搞，', '后面栽进去的时候，', '可能就有多彻底。'],
  9: ['他最危险的地方，', '是你前面十集可能都没发现，', '他已经认真了。'],
  10: ['你若同意，', '我便护你一世无忧。'],
  11: ['他不保证为你与全世界为敌，', '他比较想先带你俩，', '活着离开。'],
  12: ['你以为系统给你匹配了两个人，', '后来发现麻烦了——', '都是他。'],
  13: ['别人家的英雄排场很大，', '你家的比较朴素——', '但是妖来了他真能上。'],
  14: ['你以为自己在和他暧昧，', '他可能连你下一步会怎么嘴硬，', '都猜到了。'],
  15: ['他最难搞的地方不是危险，', '是一旦真的相信你，', '就会把这件事看得太重。'],
  16: ['你还在想怎么办，', '他可能已经骑马出城了。'],
  17: ['他原本只想算计你，', '后来发现最难算的那一步是：', '自己认真了。'],
  18: ['你需要的可能不是来救你的英雄，', '而是那个终于看懂你，', '为什么一直这么累的人。'],
  19: ['系统确认你的盖世英雄已经到了。', '唯一的问题是，', '他目前比较关心谁是天下第一。'],
  20: ['他不是最会留下的那个人，', '却可能是替你挡掉最多事情、', '又最不想让你知道的人。'],
  21: ['他不会许你轰烈的誓言，', '却会记得你所有的', '喜好与不安。'],
  22: ['他不是不爱你，', '他只是想要的世界，', '可能比一段爱情大得多。'],
  23: ['你以为这场相遇是偶然？', '不，是我策划了千年的劫。'],
  24: ['全世界都觉得他很难哄，', '只有你发现：', '好像也没有那么难。'],
  25: ['他的偏爱非常明显。', '问题是，', '他的仇也非常明显。'],
};

export function getResultCoverConfig(hero: Hero): ResultCoverConfig {
  return {
    titleLayout: dualTitleIds.has(hero.id) ? 'dual' : 'vertical',
    titleAsset: titleAssets[hero.id],
    lightArtwork: lightArtworkIds.has(hero.id),
    titleContrast: darkTitleContrastIds.has(hero.id) ? 'dark' : 'light',
    scoreContrast: darkScoreContrastIds.has(hero.id) ? 'dark' : 'light',
    themeClass: themeClasses[hero.theme],
    quoteLines: quoteLines[hero.id] ?? [hero.one_liner],
  };
}

export function getTitleParts(hero: Hero) {
  if (hero.id === 12) return ['南珩', '离十六'];
  return [hero.name];
}
